import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    TextField,
    Divider,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import CalculateIcon from '@mui/icons-material/Calculate';
import HistoryIcon from '@mui/icons-material/History';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { exportToCsv } from '../utils/csvExport';

// Canadian / Standard Currency Denominations
const DENOMINATIONS = [
    { key: 'b100', label: '$100 Bills', value: 100.00, isBill: true },
    { key: 'b50', label: '$50 Bills', value: 50.00, isBill: true },
    { key: 'b20', label: '$20 Bills', value: 20.00, isBill: true },
    { key: 'b10', label: '$10 Bills', value: 10.00, isBill: true },
    { key: 'b5', label: '$5 Bills', value: 5.00, isBill: true },
    { key: 'c2', label: '$2 Toonies', value: 2.00, isBill: false },
    { key: 'c1', label: '$1 Loonies', value: 1.00, isBill: false },
    { key: 'c025', label: '$0.25 Quarters', value: 0.25, isBill: false },
    { key: 'c010', label: '$0.10 Dimes', value: 0.10, isBill: false },
    { key: 'c005', label: '$0.05 Nickels', value: 0.05, isBill: false }
];

export default function CashManagement() {
    const queryClient = useQueryClient();
    const [currentTill, setCurrentTill] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Float Denomination Counts: { [key]: count }
    const [counts, setCounts] = useState({
        b100: 0, b50: 0, b20: 5, b10: 5, b5: 8,
        c2: 5, c1: 10, c025: 20, c010: 20, c005: 20
    });

    // Modals
    const [openTillModalOpen, setOpenTillModalOpen] = useState(false);
    const [closeTillModalOpen, setCloseTillModalOpen] = useState(false);
    const [payActionModalOpen, setPayActionModalOpen] = useState(false);
    const [payActionType, setPayActionType] = useState('PAY_OUT'); // 'PAY_OUT' or 'PAY_IN'
    const [payAmount, setPayAmount] = useState('');
    const [payCategory, setPayCategory] = useState('SUPPLIES');
    const [payNotes, setPayNotes] = useState('');
    const [endingActualCash, setEndingActualCash] = useState('');
    const [statusBanner, setStatusBanner] = useState('');

    // Activity Log / Ledger
    const [transactions, setTransactions] = useState([
        { id: 1, timestamp: '10:00 AM', type: 'OPEN_FLOAT', category: 'Opening Float', amount: 200.00, user: 'Rahul (Cashier)', notes: 'Standard morning shift float' },
        { id: 2, timestamp: '11:15 AM', type: 'CASH_SALE', category: 'POS Sale #CHK-890', amount: 34.50, user: 'Rahul (Cashier)', notes: 'Cash sale received' },
        { id: 3, timestamp: '12:30 PM', type: 'PAY_OUT', category: 'Produce Emergency Run', amount: -28.00, user: 'Priya (Manager)', notes: 'Fresh cilantro from local grocer' },
        { id: 4, timestamp: '01:45 PM', type: 'CASH_SALE', category: 'POS Sale #CHK-894', amount: 92.20, user: 'Rahul (Cashier)', notes: 'Table 2 cash payment' }
    ]);

    // Calculate total from denomination counts
    const computedFloatTotal = useMemo(() => {
        return DENOMINATIONS.reduce((sum, denom) => {
            const count = Number(counts[denom.key]) || 0;
            return sum + (count * denom.value);
        }, 0);
    }, [counts]);

    useEffect(() => {
        let mounted = true;
        const fetchTill = async () => {
            try {
                const res = await api.get('/cash/current');
                if (mounted) {
                    if (res.data && res.data.status === 'OPEN') {
                        setCurrentTill(res.data);
                    } else {
                        setCurrentTill({ status: 'CLOSED', startingCash: 0 });
                    }
                    setIsLoading(false);
                }
            } catch (err) {
                if (mounted) {
                    setCurrentTill({ status: 'CLOSED', startingCash: 0 });
                    setIsLoading(false);
                }
            }
        };
        fetchTill();
        return () => { mounted = false; };
    }, []);

    const openTillMutation = useMutation({
        mutationFn: (amount) => api.post('/cash/open', { startingCash: amount, shiftId: 1 }),
        onSuccess: (res) => {
            setCurrentTill(res.data || { status: 'OPEN', startingCash: computedFloatTotal });
            queryClient.invalidateQueries(['currentTill']);
            setOpenTillModalOpen(false);
            setStatusBanner(`Till successfully OPENED with float of $${computedFloatTotal.toFixed(2)}.`);
            // Add to activity ledger
            setTransactions(prev => [
                { id: Date.now(), timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), type: 'OPEN_FLOAT', category: 'Opening Float', amount: computedFloatTotal, user: 'Active Cashier', notes: 'Drawer opened' },
                ...prev
            ]);
        }
    });

    const closeTillMutation = useMutation({
        mutationFn: (amount) => api.post('/cash/close', { actualEndingCash: amount }),
        onSuccess: () => {
            setCurrentTill({ status: 'CLOSED', startingCash: 0 });
            queryClient.invalidateQueries(['currentTill']);
            setCloseTillModalOpen(false);
            setStatusBanner(`Till CLOSED. Ending cash of $${Number(endingActualCash).toFixed(2)} reconciled.`);
        }
    });

    // Shift Telemetry
    const shiftTelemetry = useMemo(() => {
        const starting = Number(currentTill?.startingCash) || 200.00;
        const cashSales = transactions.filter(t => t.type === 'CASH_SALE').reduce((sum, t) => sum + t.amount, 0);
        const paidIn = transactions.filter(t => t.type === 'PAY_IN').reduce((sum, t) => sum + t.amount, 0);
        const paidOut = Math.abs(transactions.filter(t => t.type === 'PAY_OUT').reduce((sum, t) => sum + t.amount, 0));
        const expectedDrawer = starting + cashSales + paidIn - paidOut;

        return {
            starting,
            cashSales,
            paidIn,
            paidOut,
            expectedDrawer
        };
    }, [currentTill, transactions]);

    const handleDenomChange = (key, deltaOrVal) => {
        setCounts(prev => {
            const current = prev[key] || 0;
            const updated = typeof deltaOrVal === 'number' && deltaOrVal < 0
                ? Math.max(0, current + deltaOrVal)
                : typeof deltaOrVal === 'number'
                ? current + deltaOrVal
                : Math.max(0, parseInt(deltaOrVal) || 0);
            return { ...prev, [key]: updated };
        });
    };

    const handleConfirmPayAction = () => {
        const amt = parseFloat(payAmount);
        if (!amt || amt <= 0) return;

        const signedAmount = payActionType === 'PAY_OUT' ? -amt : amt;
        const newTx = {
            id: Date.now(),
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            type: payActionType,
            category: payCategory,
            amount: signedAmount,
            user: 'Active Cashier',
            notes: payNotes || (payActionType === 'PAY_OUT' ? 'Cash payout' : 'Cash injection')
        };

        setTransactions(prev => [newTx, ...prev]);
        setPayActionModalOpen(false);
        setPayAmount('');
        setPayNotes('');
        setStatusBanner(`Recorded ${payActionType === 'PAY_OUT' ? 'Pay-Out of $' + amt.toFixed(2) : 'Pay-In of $' + amt.toFixed(2)} (${payCategory}).`);
    };

    const handleExportLedger = () => {
        const headers = ['Timestamp', 'Type', 'Category', 'User', 'Amount ($)', 'Notes'];
        const rows = transactions.map(t => [
            t.timestamp,
            t.type,
            t.category,
            t.user,
            t.amount.toFixed(2),
            t.notes
        ]);
        exportToCsv(`cash_ledger_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
    };

    if (isLoading) return <CircularProgress />;

    const isOpen = currentTill && currentTill.status === 'OPEN';

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        Cash Drawer Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Shift opening floats, denomination counting, cash drops, and blind reconciliation.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportLedger}
                    >
                        Export Ledger
                    </Button>
                    {!isOpen ? (
                        <Button
                            variant="contained"
                            color="success"
                            size="large"
                            startIcon={<LockOpenIcon />}
                            onClick={() => setOpenTillModalOpen(true)}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Open Shift Till
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="warning"
                            size="large"
                            startIcon={<LockIcon />}
                            onClick={() => {
                                setEndingActualCash(shiftTelemetry.expectedDrawer.toFixed(2));
                                setCloseTillModalOpen(true);
                            }}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Reconcile & Close Till
                        </Button>
                    )}
                </Box>
            </Box>

            {statusBanner && (
                <Alert severity="success" onClose={() => setStatusBanner('')} sx={{ mb: 3 }}>
                    {statusBanner}
                </Alert>
            )}

            {/* Shift Status Banner & Telemetry Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            borderLeft: 4,
                            borderLeftColor: isOpen ? 'success.main' : 'text.disabled'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <PointOfSaleIcon color={isOpen ? 'success' : 'action'} fontSize="small" />
                            <Typography variant="caption" fontWeight="bold">TILL STATUS</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color={isOpen ? 'success.main' : 'text.secondary'}>
                            {isOpen ? 'DRAWER OPEN' : 'DRAWER CLOSED'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {isOpen ? 'Active cashier shift' : 'No active till shift'}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', mb: 0.5 }}>
                            <AccountBalanceWalletIcon fontSize="small" />
                            <Typography variant="caption" fontWeight="bold">STARTING FLOAT</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold">
                            ${shiftTelemetry.starting.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Opening base cash
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main', mb: 0.5 }}>
                            <PaymentsIcon fontSize="small" />
                            <Typography variant="caption" fontWeight="bold">CASH SALES</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                            +${shiftTelemetry.cashSales.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Collected at register
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main', mb: 0.5 }}>
                            <RemoveCircleOutlineIcon fontSize="small" />
                            <Typography variant="caption" fontWeight="bold">PAID OUT / DROPS</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color="warning.main">
                            -${shiftTelemetry.paidOut.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Supplies & safe drops
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', borderColor: 'primary.main' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'info.main', mb: 0.5 }}>
                            <CalculateIcon fontSize="small" />
                            <Typography variant="caption" fontWeight="bold">EXPECTED DRAWER</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color="primary.main">
                            ${shiftTelemetry.expectedDrawer.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Should be in till right now
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Quick Actions Row */}
            {isOpen && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        Till Actions:
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<RemoveCircleOutlineIcon />}
                        onClick={() => {
                            setPayActionType('PAY_OUT');
                            setPayCategory('SUPPLIES');
                            setPayActionModalOpen(true);
                        }}
                    >
                        Cash Pay-Out / Drop
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => {
                            setPayActionType('PAY_IN');
                            setPayCategory('FLOAT_ADDITION');
                            setPayActionModalOpen(true);
                        }}
                    >
                        Cash Pay-In (Add Cash)
                    </Button>
                </Paper>
            )}

            {/* Cash Activity Ledger Table */}
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            Cash Activity Audit Ledger
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {transactions.length} Shift Transactions Recorded
                    </Typography>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: 'action.hover' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Transaction Type</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Category / Source</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Staff User</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.map(tx => (
                                <TableRow key={tx.id} hover>
                                    <TableCell>{tx.timestamp}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={tx.type}
                                            size="small"
                                            color={tx.amount > 0 ? 'success' : tx.amount < 0 ? 'warning' : 'default'}
                                            variant="outlined"
                                            sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{tx.category}</TableCell>
                                    <TableCell>{tx.user}</TableCell>
                                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{tx.notes}</TableCell>
                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: tx.amount >= 0 ? 'success.main' : 'warning.main' }}>
                                        {tx.amount >= 0 ? `+$${tx.amount.toFixed(2)}` : `-$${Math.abs(tx.amount).toFixed(2)}`}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Open Till Float Denomination Counter Modal */}
            <Dialog open={openTillModalOpen} onClose={() => setOpenTillModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalculateIcon color="success" />
                        <Typography variant="h6" fontWeight="bold">
                            Count Starting Float
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setOpenTillModalOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Count bills and coins physically in the drawer to establish the verified opening float balance.
                    </Typography>

                    <Grid container spacing={1.5}>
                        {DENOMINATIONS.map(d => {
                            const count = counts[d.key] || 0;
                            const sub = count * d.value;
                            return (
                                <Grid item xs={6} key={d.key}>
                                    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                            {d.label}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={count}
                                                onChange={(e) => handleDenomChange(d.key, e.target.value)}
                                                inputProps={{ min: 0 }}
                                                sx={{ width: 80 }}
                                            />
                                            <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, textAlign: 'right' }}>
                                                ${sub.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold">
                            Total Opening Float:
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                            ${computedFloatTotal.toFixed(2)}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenTillModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        disabled={computedFloatTotal <= 0}
                        onClick={() => openTillMutation.mutate(computedFloatTotal)}
                    >
                        Confirm & Open Till (${computedFloatTotal.toFixed(2)})
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Pay-Out / Pay-In Modal */}
            <Dialog open={payActionModalOpen} onClose={() => setPayActionModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {payActionType === 'PAY_OUT' ? 'Record Cash Pay-Out / Drop' : 'Record Cash Pay-In'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            label="Amount ($)"
                            type="number"
                            value={payAmount}
                            onChange={(e) => setPayAmount(e.target.value)}
                            placeholder="0.00"
                            fullWidth
                            autoFocus
                        />
                        <FormControl fullWidth>
                            <InputLabel>Reason / Category</InputLabel>
                            <Select
                                value={payCategory}
                                label="Reason / Category"
                                onChange={(e) => setPayCategory(e.target.value)}
                            >
                                {payActionType === 'PAY_OUT' ? [
                                    <MenuItem key="sup" value="SUPPLIES">Emergency Kitchen Supplies</MenuItem>,
                                    <MenuItem key="drop" value="SAFE_DROP">Mid-Shift Bank Safe Drop</MenuItem>,
                                    <MenuItem key="tip" value="TIP_PAYOUT">Cash Tips Out</MenuItem>,
                                    <MenuItem key="other" value="OTHER">Other Vendor Payout</MenuItem>
                                ] : [
                                    <MenuItem key="float" value="FLOAT_ADDITION">Additional Change Float</MenuItem>,
                                    <MenuItem key="ref" value="REFUND">Petty Cash Refund</MenuItem>
                                ]}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Cashier Notes / Recipient"
                            value={payNotes}
                            onChange={(e) => setPayNotes(e.target.value)}
                            placeholder="e.g. Paid delivery driver for mint leaves"
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPayActionModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={payActionType === 'PAY_OUT' ? 'warning' : 'primary'}
                        onClick={handleConfirmPayAction}
                    >
                        Submit {payActionType === 'PAY_OUT' ? 'Pay-Out' : 'Pay-In'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* End of Shift Close Till Reconciliation Modal */}
            <Dialog open={closeTillModalOpen} onClose={() => setCloseTillModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Blind Close Till Reconciliation</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">Expected Cash:</Typography>
                                <Typography variant="body2" fontWeight="bold">${shiftTelemetry.expectedDrawer.toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Entered Actual Cash:</Typography>
                                <Typography variant="body2" fontWeight="bold">${(parseFloat(endingActualCash) || 0).toFixed(2)}</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Discrepancy (Over / Short):</Typography>
                                <Chip
                                    label={`${((parseFloat(endingActualCash) || 0) - shiftTelemetry.expectedDrawer) >= 0 ? '+' : ''}$${(((parseFloat(endingActualCash) || 0) - shiftTelemetry.expectedDrawer)).toFixed(2)}`}
                                    color={Math.abs((parseFloat(endingActualCash) || 0) - shiftTelemetry.expectedDrawer) < 0.01 ? 'success' : 'error'}
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                        </Paper>

                        <TextField
                            label="Actual Ending Cash Count ($)"
                            type="number"
                            value={endingActualCash}
                            onChange={(e) => setEndingActualCash(e.target.value)}
                            fullWidth
                            autoFocus
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCloseTillModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => closeTillMutation.mutate(parseFloat(endingActualCash) || 0)}
                    >
                        Confirm & Close Drawer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
